import { ClientRoute } from "./ClientRoute";

import GeneralLayoutWrapper from "@/components/layout/GeneralLayoutWrapper";
import { ROUTES } from "@/constants/routes";
import AddCampaignJobBoardPage from "@/features/campaigns/jobboards/pages/AddCampaignJobBoardPage";
import CampaignJobBoardDetailPage from "@/features/campaigns/jobboards/pages/CampaignJobBoardDetailPage";
import EditCampaignJobBoardPage from "@/features/campaigns/jobboards/pages/EditCampaignJobBoardPage";
import AddCampaignPage from "@/features/campaigns/pages/AddCampaignPage";
import CampaignDetailPage from "@/features/campaigns/pages/CampaignDetailPage";
import CampaignsPage from "@/features/campaigns/pages/CampaignsPage";
import EditCampaignPage from "@/features/campaigns/pages/EditCampaignPage";
import CaptainBillingPage from "@/features/captain-billing/pages/CaptainBillingPage";
import EditClientPage from "@/features/clients/pages/edit/EditClientPage";
import OverviewClientPage from "@/features/clients/pages/overview/OverviewClientPage";
import { HomePage } from "@/features/home";
import { InsightsPage } from "@/features/insights";
import { IntegrationDetailPage, IntegrationsPage } from "@/features/integrations";
import { integrationsRoutes } from "@/features/integrations/integration-routes";
import ApplyDetailPage from "@/features/integrations/modules/applies/pages/detail/ApplyDetailPage";
import AppliesPage from "@/features/integrations/modules/applies/pages/overview/AppliesPage";
import CandidatesPage from "@/features/integrations/modules/candidates/pages/overview/CandidatesPage";
import KnowledgeDocumentCreatePage from "@/features/integrations/modules/chat/pages/KnowledgeDocumentCreatePage";
import KnowledgeDocumentDetailPage from "@/features/integrations/modules/chat/pages/KnowledgeDocumentDetailPage";
import ModuleDetailPage from "@/features/integrations/modules/pages/ModuleDetailPage";
import GeneralReportingPage from "@/features/integrations/modules/reporting/pages/GeneralReporting/GeneralReportingPage";
import { decodeSlug } from "@/features/integrations/utils/slug";
import InvoiceChannelPage from "@/features/invoices/pages/InvoiceChannelPage";
import InvoiceChannelsPage from "@/features/invoices/pages/InvoiceChannelsPage";
import InvoicedChannelsPage from "@/features/invoices/pages/InvoicedChannelsPage";
import InvoiceIntegrationModulePage from "@/features/invoices/pages/InvoiceIntegrationModulePage";
import InvoiceIntegrationModulesPage from "@/features/invoices/pages/InvoiceIntegrationModulesPage";
import InvoicesOverviewPage from "@/features/invoices/pages/InvoicesOverviewPage";
import { notificationSettingsRoute } from "@/features/notifications/notification-settings-routes";
import NotificationsPage from "@/features/notifications/pages/NotificationsPage";
import { SEGMENT_MODE } from "@/features/segments/builder/constants";
import { SegmentBuilderPage } from "@/features/segments/builder/pages/SegmentBuilderPage";
import OverviewSegmentsPage from "@/features/segments/pages/overview/OverviewSegmentsPage";
import TransactionsPage from "@/features/transactions/pages/TransactionsPage";
import AddUserPage from "@/features/users/pages/add/AddUserPage";
import EditUserPage from "@/features/users/pages/edit/EditUserPage";
import OverviewUsersPage from "@/features/users/pages/overview/OverviewUsersPage";
import OverviewVacanciesPage from "@/features/vacancies/pages/overview/OverviewVacanciesPage";
import WebAnalyticsPage from "@/features/web-analytics/pages/WebAnalyticsPage";
import { Action } from "@/lib/hooks/usePermissionMatrix";
import BudgetPlanningCampaignPage from "@/pages/budgetPlanning/BudgetPlanningCampaignPage";
import BudgetPlanningPage from "@/pages/budgetPlanning/BudgetPlanningPage";
import AddClientPage from "@/pages/client/AddClientPage";
import ClientDescriptionsPage from "@/pages/client/ClientDescriptionsPage";
import VacancyDetailPage from "@/pages/vacancies/details/VacancyDetailPage";
import { PageBaseProps } from "@/types/base-page";
import { RouteConfig } from "@/types/route-types";

// Admin-only routes configuration
export const getAdminRoutes = ({ tenantId: _tenantId }: PageBaseProps): RouteConfig[] =>
  [
    {
      path: ROUTES.clients,

      children: [
        {
          index: true,
          element: OverviewClientPage,
          routeConfigurations: {
            title: ({ t }) => t("common.clients"),
            breadcrumb: ({ t }) => ({
              currentPage: { label: t("common.clients") },
            }),
          },
        },
        { path: ROUTES.new, element: AddClientPage },
        {
          path: ":clientId",
          element: <ClientRoute />,
          children: [
            {
              index: true,
              redirect: ROUTES.home,
              routeConfigurations: { title: ({ t }) => t("routes.client.home") },
            },
            {
              path: ROUTES.home,
              element: HomePage,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("routes.client.home"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("routes.client.home") },
                }),
              },
            },
            {
              path: ROUTES.descriptions,
              element: ClientDescriptionsPage,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("common.descriptions"),
              },
            },
            {
              path: ROUTES.edit,
              element: <EditClientPage />,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("admin.client.editClient"),
                breadcrumb: ({ params, t }) => ({
                  currentPage: { label: t("admin.client.editClient") },
                  navigation: [
                    {
                      label: t("common.clients"),
                      url: `/${ROUTES.clients}`,
                    },
                    {
                      label: t("common.client"),
                      url: `/${ROUTES.clients}/${params.clientId}`,
                    },
                  ],
                }),
              },
              permission: {
                entity: "clients",
                action: Action.EDIT,
                fallbackPath: `${ROUTES.clients}`,
              },
            },
            {
              path: ROUTES.users,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("admin.userManagement.title"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("admin.userManagement.title") },
                }),
              },

              children: [
                {
                  index: true,
                  element: OverviewUsersPage,
                },
                {
                  path: ROUTES.new,
                  element: AddUserPage,
                  routeConfigurations: {
                    title: ({ t }) => t("admin.userManagement.addUser.title"),
                    breadcrumb: ({ params, t }) => ({
                      currentPage: {
                        label: t("admin.userManagement.addUser.title"),
                      },
                      navigation: [
                        {
                          label: t("admin.userManagement.title"),
                          url: `/${ROUTES.clients}/${params.clientId}/${ROUTES.users}`,
                        },
                      ],
                    }),
                  },
                },
                {
                  path: ":userId",
                  children: [
                    { index: true, element: EditUserPage },
                    {
                      path: ROUTES.edit,
                      element: EditUserPage,
                      routeConfigurations: {
                        title: ({ t }) =>
                          t("admin.userManagement.editUser.title"),
                        breadcrumb: ({ params, t }) => ({
                          currentPage: {
                            label: t("admin.userManagement.editUser.title"),
                          },
                          navigation: [
                            {
                              label: t("admin.userManagement.title"),
                              url: `/${ROUTES.clients}/${params.clientId}`,
                            },
                          ],
                        }),
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: ROUTES.segments,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("common.segments"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("common.segments") },
                }),
              },
              children: [
                { index: true, element: OverviewSegmentsPage },
                {
                  path: ROUTES.new,
                  element: <SegmentBuilderPage mode={SEGMENT_MODE.EDIT} />,
                  permission: {
                    entity: "segments",
                    action: Action.CREATE,
                    fallbackPath: `${ROUTES.clients}`,
                  },
                  routeConfigurations: {
                    excludeInnerLayout: true,
                    title: ({ t }) => t("admin.segments.create"),
                    breadcrumb: ({ params, t }) => ({
                      currentPage: { label: t("admin.segments.create") },
                      navigation: [
                        {
                          label: t("common.segments"),
                          url: `/${ROUTES.clients}/${params.clientId}/${ROUTES.segments}`,
                        },
                      ],
                    }),
                  },
                },
                {
                  path: ":segmentId",
                  routeConfigurations: {
                    excludeInnerLayout: true,
                  },
                  children: [
                    {
                      index: true,
                      element: <SegmentBuilderPage mode={SEGMENT_MODE.VIEW} />,
                      permission: {
                        entity: "segments",
                        action: Action.VIEW,
                        fallbackPath: `${ROUTES.clients}`,
                      },
                      routeConfigurations: {
                        title: ({ t }) => t("common.viewSegment"),
                        breadcrumb: ({ params, t }) => ({
                          currentPage: { label: t("common.viewSegment") },
                          navigation: [
                            {
                              label: t("common.segments"),
                              url: `/${ROUTES.clients}/${params.clientId}/${ROUTES.segments}`,
                            },
                          ],
                        }),
                      },
                    },
                    {
                      path: ROUTES.edit,
                      element: <SegmentBuilderPage mode={SEGMENT_MODE.EDIT} />,
                      permission: {
                        entity: "segments",
                        action: Action.EDIT,
                        fallbackPath: `${ROUTES.clients}`,
                      },
                      routeConfigurations: {
                        title: ({ t }) => t("common.editSegment"),
                        breadcrumb: ({ params, t }) => ({
                          currentPage: { label: t("common.editSegment") },
                          navigation: [
                            {
                              label: t("common.segments"),
                              url: `/${ROUTES.clients}/${params.clientId}/${ROUTES.segments}`,
                            },
                          ],
                        }),
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: ROUTES.vacancies,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("common.vacancies"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("common.vacancies") },
                }),
              },
              children: [
                { index: true, element: OverviewVacanciesPage },
                {
                  path: ":vacancyId",
                  children: [{ index: true, element: VacancyDetailPage }],
                },
              ],
            },
            {
              path: ROUTES.campaigns,
              routeConfigurations: {
                title: ({ t }) => t("common.campaigns"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("common.campaigns") },
                }),
                excludeInnerLayout: true,
              },
              children: [
                {
                  index: true,
                  element: CampaignsPage,
                  routeConfigurations: {
                    excludeInnerLayout: true,
                  },
                },
                {
                  path: ROUTES.new,
                  element: AddCampaignPage,
                  routeConfigurations: {
                    title: ({ t }) => t("admin.campaigns.add.title"),
                    breadcrumb: ({ params, t }) => ({
                      currentPage: { label: t("common.add") },
                      navigation: [
                        {
                          label: t("common.campaigns"),
                          url: `/${ROUTES.clients}/${params.clientId}/${ROUTES.campaigns}`,
                        },
                      ],
                    }),
                  },
                },
                {
                  path: ":campaignId",
                  children: [
                    { index: true, element: CampaignDetailPage },
                    { path: ROUTES.edit, element: EditCampaignPage },
                    {
                      path: ROUTES.campaignIntegrations,
                      children: [
                        {
                          path: ROUTES.new,
                          element: AddCampaignJobBoardPage,
                        },
                        {
                          path: ":clientIntegrationModuleId",
                          children: [
                            {
                              index: true,
                              element: CampaignJobBoardDetailPage,
                            },
                            {
                              path: ROUTES.edit,
                              element: EditCampaignJobBoardPage,
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              path: ROUTES.transactions,

              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("common.transactions"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("common.transactions") },
                }),
              },
              children: [{ index: true, element: TransactionsPage }],
            },
            {
              path: ROUTES.budgetPlanning,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("common.budgetPlanning"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("common.budgetPlanning") },
                }),
              },
              children: [
                {
                  path: ":year/:month",
                  children: [
                    { index: true, element: BudgetPlanningPage },
                    {
                      path: ":campaignId",
                      element: BudgetPlanningCampaignPage,
                    },
                  ],
                },
              ],
            },
            {
              path: ROUTES.insights,
              wrapper: GeneralLayoutWrapper,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("common.insights"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("common.insights") },
                }),
              },
              children: [{ index: true, element: InsightsPage }],
            },
            {
              // Optional :level? segment — see comment in client-routes.tsx.
              path: `${ROUTES.reporting}/:level?`,
              element: GeneralReportingPage,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("integrations.modules.names.reporting", "Reporting"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("integrations.modules.names.reporting", "Reporting") },
                }),
              },
            },
            {
              path: ROUTES.applies,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("common.applies"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("common.applies") },
                }),
              },
              children: [
                { index: true, element: AppliesPage },
                // Static "candidates" outranks ":applyId" in RR v6 ranking (OR-2155).
                { path: ROUTES.candidates, element: CandidatesPage },
                { path: ":applyId", element: ApplyDetailPage },
              ],
            },
            {
              path: ROUTES.webAnalytics,
              element: WebAnalyticsPage,
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("webAnalytics.title"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("webAnalytics.title") },
                }),
              },
            },
            {
              path: ROUTES.tokenManagement,
              element: CaptainBillingPage,
              permission: {
                entity: "captainBilling",
                action: Action.VIEW,
                fallbackPath: `${ROUTES.clients}`,
              },
              routeConfigurations: {
                excludeInnerLayout: true,
                title: ({ t }) => t("captainBilling.title"),
              },
            },
            {
              path: ROUTES.integrations,
              routeConfigurations: {
                excludeInnerLayout: true,
              },
              children: [
                { index: true, element: IntegrationsPage },
                {
                  path: ":integrationName",
                  element: IntegrationDetailPage,
                },
                {
                  path: ":integrationName/:moduleName",
                  element: ModuleDetailPage,
                  routeConfigurations: {
                    excludeInnerLayout: true,
                    title: ({ t, params }) =>
                      t(
                        `integrations.modules.names.${decodeSlug(
                          params.moduleName ?? "",
                        )}`,
                      ),
                  },
                },
                {
                  path: ":integrationName/:moduleName/new",
                  element: KnowledgeDocumentCreatePage,
                  routeConfigurations: {
                    excludeInnerLayout: true,
                  },
                },
                {
                  path: ":integrationName/:moduleName/:fileName",
                  element: KnowledgeDocumentDetailPage,
                  routeConfigurations: {
                    excludeInnerLayout: true,
                  },
                },
              ],
            },
            integrationsRoutes,
          ],
        },
      ],
    },
    integrationsRoutes,
    {
      path: ROUTES.invoices,
      routeConfigurations: {
        excludeInnerLayout: true,
      },
      children: [
        {
          index: true,
          redirect: ROUTES.integrations,
        },
        {
          element: InvoicesOverviewPage,
          children: [
            {
              path: ROUTES.integrations,
              children: [{ index: true, element: InvoiceIntegrationModulesPage }],
            },
            {
              path: ROUTES.channels,
              children: [{ index: true, element: InvoiceChannelsPage }],
            },
          ],
        },
        {
          path: `${ROUTES.integrations}/:tenantIntegrationModuleId`,
          children: [
            {
              path: `${ROUTES.toDo}/:year/:month`,
              element: InvoiceIntegrationModulePage,
            },
            {
              path: `${ROUTES.invoiced}/:year/:month`,
              element: InvoiceIntegrationModulePage,
            },
          ],
        },
        {
          path: `${ROUTES.channels}/:channelId`,
          children: [
            {
              path: `${ROUTES.toDo}/:year/:month`,
              element: InvoiceChannelPage,
            },
            {
              path: `${ROUTES.invoiced}/:year/:month`,
              element: InvoicedChannelsPage,
            },
          ],
        },
      ],
    },
    {
      path: ROUTES.applies,
      routeConfigurations: {
        excludeInnerLayout: true,
      },
      children: [
        { index: true, element: AppliesPage },
        // Static "candidates" outranks ":applyId" in RR v6 ranking (OR-2155).
        { path: ROUTES.candidates, element: CandidatesPage },
        { path: ":applyId", element: ApplyDetailPage },
      ],
    },
    {
      path: ROUTES.users,
      routeConfigurations: {
        excludeInnerLayout: true,
      },

      children: [
        {
          index: true,
          element: OverviewUsersPage,
          routeConfigurations: {
            title: ({ t }) => t("admin.userManagement.title"),
            breadcrumb: ({ t }) => ({
              currentPage: { label: t("admin.userManagement.title") },
            }),
          },
        },
        {
          path: ROUTES.new,
          element: AddUserPage,
          routeConfigurations: {
            title: ({ t }) => t("admin.userManagement.addUser.title"),
            breadcrumb: ({ t }) => ({
              currentPage: { label: t("admin.userManagement.addUser.title") },
              navigation: [
                {
                  label: t("admin.userManagement.title"),
                  url: `/${ROUTES.users}`,
                },
              ],
            }),
          },
        },
        {
          path: ":userId",
          children: [
            {
              index: true,
              element: EditUserPage,
              routeConfigurations: {
                title: ({ t }) => t("admin.userManagement.editUser.title"),
                breadcrumb: ({ t }) => ({
                  currentPage: {
                    label: t("admin.userManagement.editUser.title"),
                  },
                  navigation: [
                    {
                      label: t("admin.userManagement.title"),
                      url: `/${ROUTES.users}`,
                    },
                  ],
                }),
              },
            },
            {
              path: ROUTES.edit,
              element: EditUserPage,
              routeConfigurations: {
                title: ({ t }) => t("admin.userManagement.editUser.title"),
                breadcrumb: ({ t }) => ({
                  currentPage: {
                    label: t("admin.userManagement.editUser.title"),
                  },
                  navigation: [
                    {
                      label: t("admin.userManagement.title"),
                      url: `/${ROUTES.users}`,
                    },
                  ],
                }),
              },
            },
          ],
        },
      ],
    },
    {
      path: ROUTES.insights,
      element: <InsightsPage />,
    },
    {
      path: ROUTES.notifications,
      element: NotificationsPage,
      routeConfigurations: {
        excludeInnerLayout: true,
        title: ({ t }) => t("notifications.title"),
        breadcrumb: ({ t }) => ({
          currentPage: { label: t("notifications.title") },
        }),
      },
    },
    notificationSettingsRoute,
  ] as const;
